import { useEffect } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, MapPin, X } from "lucide-react";

export const Lightbox = ({ items, index, onChange, onClose, labels, lang }) => {
  const n = items.length;
  const go = (d) => onChange((index + d + n) % n);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onChange((index + 1) % n);
      if (e.key === "ArrowLeft") onChange((index - 1 + n) % n);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, n, onChange, onClose]);

  useEffect(() => {
    window.__lenis?.stop();
    document.body.style.overflow = "hidden";
    return () => {
      window.__lenis?.start();
      document.body.style.overflow = "";
    };
  }, []);

  const p = items[index];
  if (!p) return null;
  const caption = (lang === "hi" && p.caption_hi) || p.caption_en;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-[70] flex flex-col bg-ink/95 backdrop-blur-xl"
      role="dialog"
      aria-modal="true"
      data-testid="gallery-lightbox"
    >
      <div className="flex items-center justify-between px-5 py-4">
        <span className="font-mono text-xs text-slate-300" data-testid="lightbox-counter">{index + 1} / {n}</span>
        <button onClick={onClose} className="rounded-full border border-white/20 p-2.5 text-white hover:bg-white/10" aria-label={labels.close} data-testid="lightbox-close-btn"><X size={20} /></button>
      </div>
      <div className="relative flex flex-1 items-center justify-center overflow-hidden px-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <motion.img
          key={p.id}
          src={p.image}
          alt={caption || p.location || ""}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.35 }}
          drag={n > 1 ? "x" : false}
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.6}
          onDragEnd={(_, info) => {
            if (info.offset.x < -60) go(1);
            else if (info.offset.x > 60) go(-1);
          }}
          className="max-h-full max-w-full cursor-grab select-none rounded-2xl object-contain active:cursor-grabbing"
          draggable={false}
          data-testid="lightbox-image"
        />
        {n > 1 && (
          <>
            <button onClick={() => go(-1)} className="absolute left-3 top-1/2 hidden -translate-y-1/2 rounded-full border border-white/20 bg-ink/60 p-3 text-white hover:bg-white/10 sm:block" aria-label={labels.prev} data-testid="lightbox-prev-btn"><ChevronLeft size={22} /></button>
            <button onClick={() => go(1)} className="absolute right-3 top-1/2 hidden -translate-y-1/2 rounded-full border border-white/20 bg-ink/60 p-3 text-white hover:bg-white/10 sm:block" aria-label={labels.next} data-testid="lightbox-next-btn"><ChevronRight size={22} /></button>
          </>
        )}
      </div>
      <div className="px-5 pb-8 pt-4 text-center">
        {caption && <p className="font-display text-lg font-semibold text-white" data-testid="lightbox-caption">{caption}</p>}
        {p.location && <p className="mt-1 flex items-center justify-center gap-1.5 text-xs text-slate-300"><MapPin size={12} className="text-saffron" />{p.location}</p>}
        {n > 1 && (
          <div className="mt-4 flex justify-center gap-4 sm:hidden">
            <button onClick={() => go(-1)} className="rounded-full border border-white/20 p-2.5 text-white" aria-label={labels.prev} data-testid="lightbox-prev-mobile-btn"><ChevronLeft size={18} /></button>
            <button onClick={() => go(1)} className="rounded-full border border-white/20 p-2.5 text-white" aria-label={labels.next} data-testid="lightbox-next-mobile-btn"><ChevronRight size={18} /></button>
          </div>
        )}
      </div>
    </motion.div>
  );
};
